import { useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import LocalizedLink from './LocalizedLink';
import { ChevronRight, Home } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const segmentLabels: Record<string, Record<string, string>> = {
  services: { de: 'Services', en: 'Services', tr: 'Hizmetler', fr: 'Services', ru: 'Услуги', it: 'Servizi', es: 'Servicios', ar: 'الخدمات' },
  transfers: { de: 'Transfers', en: 'Transfers', tr: 'Transferler', fr: 'Transferts', ru: 'Трансферы', it: 'Trasferimenti', es: 'Traslados', ar: 'التحويلات' },
  transfer: { de: 'Transfer', en: 'Transfer', tr: 'Transfer', fr: 'Transfert', ru: 'Трансфер', it: 'Trasferimento', es: 'Traslado', ar: 'النقل' },
  tours: { de: 'Touren', en: 'Tours', tr: 'Turlar', fr: 'Excursions', ru: 'Туры', it: 'Tour', es: 'Tours', ar: 'الجولات' },
  fleet: { de: 'Fuhrpark', en: 'Fleet', tr: 'Filo', fr: 'Flotte', ru: 'Автопарк', it: 'Flotta', es: 'Flota', ar: 'الأسطول' },
  booking: { de: 'Buchung', en: 'Booking', tr: 'Rezervasyon', fr: 'Réservation', ru: 'Бронирование', it: 'Prenotazione', es: 'Reserva', ar: 'الحجز' },
  contact: { de: 'Kontakt', en: 'Contact', tr: 'İletişim', fr: 'Contact', ru: 'Контакт', it: 'Contatto', es: 'Contacto', ar: 'اتصل' },
  city: { de: 'Städte', en: 'Cities', tr: 'Şehirler', fr: 'Villes', ru: 'Города', it: 'Città', es: 'Ciudades', ar: 'المدن' },
};

const Breadcrumbs = () => {
  const { lang } = useLanguage();
  const location = useLocation();

  // Strip language prefix
  const path = location.pathname.replace(/^\/(de|en|tr|fr|ru|it|es|ar)/, '') || '/';
  if (path === '/') return null;

  const segments = path.split('/').filter(Boolean);
  const crumbs = segments.map((seg, i) => {
    const href = '/' + segments.slice(0, i + 1).join('/');
    const label = segmentLabels[seg]?.[lang] || seg.replace(/^taxi-salzburg-/, '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    return { href, label };
  });

  const allCrumbs = [{ href: '/', label: 'Home' }, ...crumbs];

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": allCrumbs.map((c, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": c.label,
      "item": `https://www.taxisalzburg.net/${lang}${c.href === '/' ? '' : c.href}`,
    })),
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>
      <nav aria-label="Breadcrumb" className="bg-muted/50 border-b border-border">
        <div className="container py-3">
          <ol className="flex items-center gap-1.5 text-sm text-muted-foreground flex-wrap">
            {allCrumbs.map((crumb, i) => (
              <li key={crumb.href} className="flex items-center gap-1.5">
                {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50" />}
                {i === allCrumbs.length - 1 ? (
                  <span className="text-foreground font-medium truncate max-w-[200px]">
                    {i === 0 && <Home className="w-3.5 h-3.5 inline mr-1" />}
                    {crumb.label}
                  </span>
                ) : (
                  <LocalizedLink
                    to={crumb.href}
                    className="hover:text-primary transition-colors truncate max-w-[200px]"
                  >
                    {i === 0 && <Home className="w-3.5 h-3.5 inline mr-1" />}
                    {crumb.label}
                  </LocalizedLink>
                )}
              </li>
            ))}
          </ol>
        </div>
      </nav>
    </>
  );
};

export default Breadcrumbs;
